/**
 * Real-time protocol between the producer, the presenter and the server.
 *
 * Every payload has a Zod schema; the server validates each on receipt (never
 * trust the wire). The typed event maps give end-to-end type safety with a typed
 * Socket.IO client and server.
 */
import { z } from 'zod';
import { DisplaySettingsSchema, LayoutPresetIdSchema, RoleSchema, SessionStateSchema, } from './model.js';
// ---------------------------------------------------------------------------
// Session create / join
// ---------------------------------------------------------------------------
export const CreatePayloadSchema = z.object({
    role: RoleSchema,
    name: z.string().max(60).default(''),
});
export const JoinPayloadSchema = z.object({
    roomCode: z.string().min(3).max(12),
    role: RoleSchema,
    name: z.string().max(60).default(''),
});
export const JoinResultSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), session: SessionStateSchema }),
    z.object({ ok: z.literal(false), error: z.string() }),
]);
/**
 * Super-admin observe: an authenticated admin watches a live session read-only and
 * may optionally assist (edit the script / send a cue). The observer is invisible —
 * it joins the room for broadcasts but is NOT added to presence.
 */
export const AdminObservePayloadSchema = z.object({
    roomCode: z.string().min(3).max(12),
});
/**
 * Consent-gated assist. An observing admin asks for control (`assist:request`); the
 * room's producer approves or refuses (`assist:decide`). Only while a grant is live
 * are the admin's control actions accepted by the server. Either side can end it:
 * the admin releases (`assist:release`) or the producer revokes (`assist:decide`
 * with allow=false). The server announces the current state via `assist:state`.
 */
export const AssistRequestSchema = z.object({ roomCode: z.string().min(3).max(12) });
export const AssistDecisionSchema = z.object({
    roomCode: z.string().min(3).max(12),
    allow: z.boolean(),
});
/** Server → producer: an admin is asking to take control. */
export const AssistRequestedSchema = z.object({ adminName: z.string() });
/** Server → room: whether an admin currently holds producer control. */
export const AssistStateSchema = z.object({
    granted: z.boolean(),
    adminName: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Recorder: transcript opt-in, version history, sign-off / archive
// ---------------------------------------------------------------------------
/** Presenter → server: one line of recognised speech (only when recording is on). */
export const TranscriptLineSchema = z.object({ text: z.string().min(1).max(2000) });
/** Producer/assisting-admin → server: turn transcript recording on/off. */
export const RecordSetSchema = z.object({ on: z.boolean() });
/** Server → room: the current recording state. */
export const RecordStateSchema = z.object({ on: z.boolean() });
/** One entry in the live version history (metadata only; project fetched on restore). */
export const HistoryEntrySchema = z.object({
    at: z.number().int(),
    version: z.number().int(),
    title: z.string(),
    by: z.string().optional(),
});
/** Restore a specific script version by its timestamp id. */
export const HistoryRestoreSchema = z.object({ at: z.number().int() });
/** Server → room: a show was signed off and archived. */
export const ArchivedSchema = z.object({ id: z.string(), title: z.string() });
// ---------------------------------------------------------------------------
// Scroll control (producer intent) + reanchor (presenter reports pixel truth)
// ---------------------------------------------------------------------------
export const ScrollActionSchema = z.enum([
    'play',
    'pause',
    'toggle',
    'setSpeed',
    'speedUp',
    'speedDown',
    'nudge', // value = block-units delta (+forward / -back)
    'top',
    'sectionPrev',
    'sectionNext',
    'goto', // blockId = target block
]);
export const ScrollControlSchema = z.object({
    action: ScrollActionSchema,
    value: z.number().optional(),
    blockId: z.string().optional(),
});
/** The presenter tells the server the concrete block/offset it is currently at. */
export const ReanchorSchema = z.object({
    blockId: z.string(),
    offsetFraction: z.number().min(0).max(1),
});
// ---------------------------------------------------------------------------
// Layout / cue / timer / settings / presence payloads
// ---------------------------------------------------------------------------
export const LayoutSetSchema = z.object({
    presetId: LayoutPresetIdSchema.optional(),
    infoRatio: z.number().min(0).max(1).optional(),
});
export const TimerControlSchema = z.object({
    which: z.enum(['production', 'segment']),
    action: z.enum(['start', 'pause', 'reset', 'setDuration', 'show', 'hide']),
    value: z.number().optional(), // ms, for setDuration
});
export const SettingsUpdateSchema = DisplaySettingsSchema.partial();
export const PresenceUpdateSchema = z.object({
    name: z.string().max(60).optional(),
    batteryPct: z.number().min(0).max(100).optional(),
    online: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Event name constants (avoid stringly-typed drift)
// ---------------------------------------------------------------------------
export const EVENTS = {
    sessionCreate: 'session:create',
    sessionJoin: 'session:join',
    adminObserve: 'admin:observe',
    adminLeave: 'admin:leave',
    assistRequest: 'assist:request',
    assistDecide: 'assist:decide',
    assistRelease: 'assist:release',
    assistRequested: 'assist:requested',
    assistState: 'assist:state',
    transcriptLine: 'transcript:line',
    recordSet: 'record:set',
    recordState: 'record:state',
    historyList: 'history:list',
    historyRestore: 'history:restore',
    sessionSignoff: 'session:signoff',
    sessionArchived: 'session:archived',
    presenceUpdate: 'presence:update',
    scriptUpdate: 'script:update',
    scrollControl: 'scroll:control',
    scrollReanchor: 'scroll:reanchor',
    layoutSet: 'layout:set',
    cueShow: 'cue:show',
    cueClear: 'cue:clear',
    timerControl: 'timer:control',
    settingsUpdate: 'settings:update',
    // server -> client
    stateSnapshot: 'state:snapshot',
    scriptUpdated: 'script:updated',
    scrollState: 'scroll:state',
    layoutUpdated: 'layout:updated',
    cueShown: 'cue:shown',
    cueCleared: 'cue:cleared',
    timersUpdated: 'timers:updated',
    settingsUpdated: 'settings:updated',
    presenceUpdated: 'presence:updated',
    sessionError: 'session:error',
};
//# sourceMappingURL=protocol.js.map